import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { Medal, Edit } from 'lucide-react';
import { db, APP_ID_INTERNO } from '../firebase';

// Importamos las otras fases para mostrarlas en modo "solo lectura"
import Phase1Swiss from './Phase1Swiss';
import PhaseTiebreaker from './PhaseTiebreaker';
import Phase2Bracket from './Phase2Bracket';
import './Phase3Results.css';

export default function Phase3Results({ tournament, role }) {
    const [view, setView] = useState('results'); // 'results', 'phase1', 'phase_tiebreaker', 'phase2', 'finances'
    const [editingPaymentId, setEditingPaymentId] = useState(null);

    const reopenTournament = async () => {
        if (role !== 'admin') return;
        if (window.confirm("¿Seguro que quieres reabrir el torneo? Podrás seguir editando los resultados de las eliminatorias.")) {
            await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id), { phase: 'phase2' });
        }
    };

    const handlePayment = async (playerId, type, method, amount) => {
        if (role !== 'admin') return;
        const players = tournament.players.map(p => 
            p.id === playerId ? { ...p, payment: { type, method, amount }, hasPaid: true } : p
        );
        await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id), { players });
        setEditingPaymentId(null);
    };

    const removePayment = async (playerId) => {
        if (role !== 'admin') return;
        const players = tournament.players.map(p => 
            p.id === playerId ? { ...p, payment: null, hasPaid: false } : p
        );
        await updateDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournament.id), { players });
        setEditingPaymentId(null);
    };

    const results = tournament.leagueResults || [];

    const cashTotal = tournament.players.reduce((sum, p) => p.payment?.method === 'cash' ? sum + p.payment.amount : sum, 0);
    const bizumTotal = tournament.players.reduce((sum, p) => p.payment?.method === 'bizum' ? sum + p.payment.amount : sum, 0);
    const grandTotal = cashTotal + bizumTotal;

    return (
        <div className="results-layout">
            {/* NAVEGADOR DE PESTAÑAS */}
            <div className="results-tabs">
                <button 
                    onClick={() => setView('results')}
                    className={`results-tab-btn ${view === 'results' ? 'is-active-results' : ''}`}
                >
                    <span className="results-tab-icon">🥇</span> Resultados
                </button>
                <button 
                    onClick={() => setView('phase1')}
                    className={`results-tab-btn ${view === 'phase1' ? 'is-active-phase1' : ''}`}
                >
                    <span className="results-tab-icon">⚔️</span> Fase Suiza
                </button>
                {tournament.tiebreakerMatches && tournament.tiebreakerMatches.length > 0 && (
                    <button 
                        onClick={() => setView('phase_tiebreaker')}
                        className={`results-tab-btn ${view === 'phase_tiebreaker' ? 'is-active-tiebreaker' : ''}`}
                    >
                        <span className="results-tab-icon">⚖️</span> Desempate
                    </button>
                )}
                {tournament.phase2Bracket && tournament.phase2Bracket.length > 0 && (
                    <button 
                        onClick={() => setView('phase2')}
                        className={`results-tab-btn ${view === 'phase2' ? 'is-active-phase2' : ''}`}
                    >
                        <span className="results-tab-icon">🏆</span> Eliminatorias
                    </button>
                )}
                {role === 'admin' && (
                    <button 
                        onClick={() => setView('finances')}
                        className={`results-tab-btn ${view === 'finances' ? 'is-active-finances' : ''}`}
                    >
                        <span className="results-tab-icon">💰</span> Finanzas
                    </button>
                )}
            </div>

            {/* VISTAS DE SOLO LECTURA */}
            {view === 'phase1' && (
                <div className="readonly-view">
                    <Phase1Swiss tournament={tournament} role="guest" />
                </div>
            )}

            {view === 'phase_tiebreaker' && (
                <div className="readonly-view">
                    <PhaseTiebreaker tournament={tournament} role="guest" />
                </div>
            )}

            {view === 'phase2' && (
                <div className="readonly-view">
                    <Phase2Bracket tournament={tournament} role="guest" />
                </div>
            )}

            {/* VISTA FINANCIERA (SOLO ADMIN) */}
            {view === 'finances' && role === 'admin' && (
                <div className="finances-view">
                    <div className="finances-cards">
                        <div className="finances-card-item cash">
                            <div className="finances-card-label">Efectivo</div>
                            <div className="finances-card-value">{cashTotal}€</div>
                        </div>
                        <div className="finances-card-item bizum">
                            <div className="finances-card-label">Bizum</div>
                            <div className="finances-card-value">{bizumTotal}€</div>
                        </div>
                        <div className="finances-card-item total">
                            <div className="finances-card-label">Total Recaudado</div>
                            <div className="finances-card-value">{grandTotal}€</div>
                        </div>
                    </div>

                    <div className="table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Blader Inscrito</th>
                                    <th className="th-center">Tipo de Entrada</th>
                                    <th className="th-center">Método de Pago</th>
                                    <th className="th-center">Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tournament.players.map((p) => (
                                    <tr key={p.id}>
                                        <td style={{textTransform: 'uppercase', fontStyle: 'italic', fontWeight: '900'}}>{p.name}</td>
                                        
                                        {editingPaymentId === p.id ? (
                                            <td colSpan="3">
                                                <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '300px', margin: '0 auto'}}>
                                                    <div className="payment-edit-grid">
                                                        <button onClick={() => handlePayment(p.id, 'child', 'cash', 2)} className="btn-pay-option cash-child">👦 2€ Efectivo</button>
                                                        <button onClick={() => handlePayment(p.id, 'child', 'bizum', 2)} className="btn-pay-option bizum-child">👦 2€ Bizum</button>
                                                        <button onClick={() => handlePayment(p.id, 'adult', 'cash', 3)} className="btn-pay-option cash-child">🧑 3€ Efectivo</button>
                                                        <button onClick={() => handlePayment(p.id, 'adult', 'bizum', 3)} className="btn-pay-option bizum-child">🧑 3€ Bizum</button>
                                                    </div>
                                                    <div style={{display: 'flex', gap: '0.5rem', justifyContent: 'flex-end'}}>
                                                        <button onClick={() => removePayment(p.id)} className="btn-pay-action neutral">Anular / Bono</button>
                                                        <button onClick={() => setEditingPaymentId(null)} className="btn-pay-action cancel">Cancelar</button>
                                                    </div>
                                                </div>
                                            </td>
                                        ) : (
                                            <>
                                                <td className="td-center">
                                                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'}}>
                                                        <span>{p.payment ? (p.payment.type === 'child' ? '👦 Niño' : '🧑 Adulto') : (p.hasPaid ? 'Pagado (Antiguo)' : '🎟️ Bono / Pendiente')}</span>
                                                        <button onClick={() => setEditingPaymentId(p.id)} style={{background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', padding: '0.5rem', cursor: 'pointer', color: 'var(--text-secondary)'}} title="Editar Pago">
                                                            <Edit className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="td-center">
                                                    {p.payment ? (p.payment.method === 'cash' ? '💵 Efectivo' : '📱 Bizum') : '-'}
                                                </td>
                                                <td className="td-center" style={{color: '#2ecc71', fontWeight: '900'}}>
                                                    {p.payment ? `+${p.payment.amount}€` : '-'}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                                {tournament.players.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="td-center" style={{color: 'var(--text-secondary)'}}>No hay jugadores registrados.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* VISTA PRINCIPAL DE RESULTADOS */}
            {view === 'results' && (
                <div className="results-view">
                    <div className="results-hero">
                        <Medal className="results-hero-icon" />
                        <h2 className="results-hero-title">Resultados Oficiales</h2>
                        <p className="results-hero-subtitle">Puntos de la Liga Alicantina calculados</p>
                    </div>

                    <div className="table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th className="th-center">Pos</th>
                                    <th>Blader</th>
                                    <th className="th-center" title="Participación en Suizo (+1)">
                                        Partic. (+1)
                                    </th>
                                    <th className="th-center" title="Victorias en Suizo (+1/u)">
                                        Vict. Suizo (+1)
                                    </th>
                                    <th className="th-center" title="Clasificado a Eliminatorias (+1)">
                                        Clasif. (+1)
                                    </th>
                                    <th className="th-center" title="Victorias en Eliminatorias (+2/u)">
                                        Vict. Elim. (+2)
                                    </th>
                                    <th className="th-center" title="Bonus Subcampeón (+2) / Campeón (+5)">
                                        Bonus Final
                                    </th>
                                    <th className="th-center" style={{color: 'var(--accent-red)'}}>
                                        Puntos Liga
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r, i) => (
                                    <tr key={r.id}>
                                        <td className="td-center">
                                            <div className={`rank-badge rank-${i === 0 ? '1' : i === 1 ? '2' : i === 2 ? '3' : 'other'}`}>
                                                {i + 1}
                                            </div>
                                        </td>
                                        <td style={{textTransform: 'uppercase', fontStyle: 'italic', fontWeight: '900', letterSpacing: '-1px'}}>{r.name}</td>
                                        <td className="td-center" style={{color: 'var(--text-secondary)'}}>{r.part}</td>
                                        <td className="td-center" style={{color: 'var(--text-secondary)'}}>{r.sWins > 0 ? r.sWins : '-'}</td>
                                        <td className="td-center" style={{color: 'var(--text-secondary)'}}>{r.qual > 0 ? r.qual : '-'}</td>
                                        <td className="td-center" style={{color: 'var(--text-secondary)'}}>{r.bWins > 0 ? r.bWins : '-'}</td>
                                        <td className="td-center">
                                            {r.champ > 0 ? <span style={{color: 'var(--accent-yellow)', fontWeight: '900'}}>+5</span> : r.runUp > 0 ? <span style={{color: '#bdc3c7', fontWeight: '900'}}>+2</span> : '-'}
                                        </td>
                                        <td className="td-center">
                                            <span className="score-highlight">{r.total}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {role === 'admin' && (
                        <div style={{display: 'flex', justifyContent: 'center', marginTop: '2rem'}}>
                            <button onClick={reopenTournament} className="btn-reopen">
                                Reabrir Torneo (Corregir resultados)
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}